// Lightweight Web Audio manager for BGM and FX with an initial user-gesture unlock.
// Hybrid approach: use Web Audio for timing-critical playback; fallback to noop when locked.
import { trimGainFor } from './loudness';

let audioCtx = null;
let unlocked = false;
let masterGain;
let musicGain;
let fxGain;
// ultimo volume impostato dagli slider: viene applicato anche ai nodi creati dopo (ensureCtx)
let desiredMusicVolume = 1;
let desiredFxVolume = 1;
const buffers = {};
let currentBgm = null;
let currentBgmSource = null;
let bgmInstances = [];
const activeSourcesByName = new Map();

function ensureCtx() {
    if (!audioCtx) {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (!masterGain) {
        masterGain = audioCtx.createGain();
        musicGain = audioCtx.createGain();
        fxGain = audioCtx.createGain();
        musicGain.gain.value = desiredMusicVolume;
        fxGain.gain.value = desiredFxVolume;
        masterGain.gain.value = 1;
        musicGain.connect(masterGain);
        fxGain.connect(masterGain);
        masterGain.connect(audioCtx.destination);
    }
}

export async function unlockAudio() {
    ensureCtx();
    try {
        //Safari su iOS, dopo una chiamata o il blocco schermo, porta il contesto in 'interrupted' (non 'suspended')
        if (audioCtx.state !== 'running' && audioCtx.state !== 'closed') {
            await audioCtx.resume();
        }
        unlocked = true;
    } catch (e) {
        // Safari may still defer; leave unlocked false so callers can retry after another gesture.
        unlocked = false;
    }
    return unlocked;
}

const pendingLoads = {};

async function loadBuffer(name, url) {
    if (buffers[name]) return buffers[name];
    //richiesta già in corso (es. primeBuffers): non scaricare e decodificare lo stesso file due volte
    if (pendingLoads[name]) return pendingLoads[name];
    ensureCtx();
    const load = (async () => {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error("Download audio fallito (" + resp.status + "): " + name);
        const arr = await resp.arrayBuffer();
        const buf = await audioCtx.decodeAudioData(arr);
        buffers[name] = buf;
        return buf;
    })();
    pendingLoads[name] = load;
    try {
        return await load;
    } finally {
        delete pendingLoads[name];
    }
}

export async function primeBuffers(entries = []) {
    if (!unlocked) return; // avoid fetch before unlock on iOS
    await Promise.all(entries.map(({ name, url }) => loadBuffer(name, url).catch(() => null)));
}

function stopCurrentBgm() {
    if (currentBgmSource) {
        try { currentBgmSource.stop(); } catch (_) { /* noop */ }
    }
    currentBgmSource = null;
    currentBgm = null;
}

// loopStart/loopEnd (secondi) delimitano la parte ripetuta: il motore audio salta da loopEnd a loopStart
// leggendo i campioni già decodificati, quindi senza buchi (a differenza di <audio loop> o di un seek)
export async function playBgm(name, url, { loop = true, offset = 0, loopStart = 0, loopEnd = 0 } = {}) {
    if (!unlocked) return;
    const buf = await loadBuffer(name, url);
    stopCurrentBgm();
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    if (loop && loopEnd > loopStart) {
        src.loopStart = loopStart;
        src.loopEnd = loopEnd;
    }
    const trim = audioCtx.createGain();
    trim.gain.value = trimGainFor(name);
    src.connect(trim);
    trim.connect(musicGain);
    src.start(0, offset);
    currentBgmSource = src;
    currentBgm = name;
    return src;
}

export function stopBgm() {
    stopCurrentBgm();
    stopAllMusic();
}

export async function playFx(name, url, { offset = 0, target = 'fx', startAt = null } = {}) {
    if (!unlocked) return;
    const buf = await loadBuffer(name, url);
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    const trim = audioCtx.createGain();
    trim.gain.value = trimGainFor(name);
    src.connect(trim);
    trim.connect(target === 'music' ? musicGain : fxGain);
    const when = startAt != null ? Math.max(0, startAt) : 0;
    src.start(when, offset);
    if (name) {
        const list = activeSourcesByName.get(name) || [];
        list.push(src);
        activeSourcesByName.set(name, list);
        src.onended = () => {
            const updated = (activeSourcesByName.get(name) || []).filter(s => s !== src);
            if (updated.length) {
                activeSourcesByName.set(name, updated);
            } else {
                activeSourcesByName.delete(name);
            }
        };
    }
    return src;
}

export function stopAudioByName(name) {
    if (!name) return;
    const list = activeSourcesByName.get(name);
    if (!list || !list.length) return;
    list.forEach(src => {
        try { src.stop(); } catch (_) { /* noop */ }
    });
    activeSourcesByName.delete(name);
}

export function stopAllTrackedAudio() {
    activeSourcesByName.forEach(list => {
        list.forEach(src => {
            try { src.stop(); } catch (_) { /* noop */ }
        });
    });
    activeSourcesByName.clear();
}

export async function getBufferDuration(name, url) {
    if (!unlocked) return 0;
    const buf = await loadBuffer(name, url);
    return buf ? buf.duration : 0;
}

export async function playBgmInstance(name, url, { loop = false, offset = 0, startAt = 0 } = {}) {
    if (!unlocked) return null;
    const buf = await loadBuffer(name, url);
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    src.connect(musicGain);
    const when = Math.max(0, startAt);
    src.start(when, offset);
    bgmInstances.push(src);
    src.onended = () => {
        bgmInstances = bgmInstances.filter(s => s !== src);
    };
    return src;
}

export function stopAllMusic() {
    bgmInstances.forEach(src => {
        try { src.stop(); } catch (_) { /* noop */ }
    });
    bgmInstances = [];
}

// Il volume si applica UNA sola volta, sui nodi gain di musica ed effetti, non sui singoli suoni:
// gli slider agiscono anche sui suoni già in riproduzione.
// Lo slider (0-1) passa da una curva quadratica: l'orecchio percepisce il volume in modo logaritmico,
// quindi con un gain lineare già a metà slider il suono sembra quasi al massimo.
function volumeToGain(v) {
    const vol = Math.max(0, Math.min(1, parseFloat(v)));
    const slider = Number.isFinite(vol) ? vol : 1;
    return slider * slider;
}

export function setMusicVolume(v) {
    desiredMusicVolume = volumeToGain(v);
    if (musicGain) musicGain.gain.value = desiredMusicVolume;
}

export function setFxVolume(v) {
    desiredFxVolume = volumeToGain(v);
    if (fxGain) fxGain.gain.value = desiredFxVolume;
}

export function isUnlocked() {
    return unlocked;
}

export function getAudioTime() {
    return audioCtx ? audioCtx.currentTime : 0;
}

export async function pauseAllAudio() {
    if (!audioCtx) return;
    try {
        if (audioCtx.state === 'running') {
            await audioCtx.suspend();
        }
    } catch (_) { /* noop */ }
}

export async function resumeAllAudio() {
    if (!audioCtx) return;
    try {
        //anche 'interrupted' (Safari iOS), non solo 'suspended'
        if (audioCtx.state !== 'running' && audioCtx.state !== 'closed') {
            await audioCtx.resume();
        }
    } catch (_) { /* noop */ }
}
